import { dayAxis } from '@/components/charts/generic/dayAxis';
import { trendSeries } from '@/components/charts/generic/seriesHelpers';
import type { MetricSeriesSpec } from '@/components/charts/generic/seriesTypes';
import type { MetricChartSpec } from '@/components/charts/generic/types';

import { daypartColor, daypartMetricSeries, trendDaypartTag } from '../daypartSeries';

import type { DaypartChartSpecParams } from './types';

// ---------------------------------------------------------------------------
// Living weight
// ---------------------------------------------------------------------------

export interface LivingWeightChartSpecParams extends DaypartChartSpecParams {
    /** Already translated axis title. */
    axisLabel: string;
    /** Already translated label for the fitted trend line. */
    trendLabel: string;
    weightUnitTag: string;
}

const WEIGHT_TICK_DECIMALS = 0;
const WEIGHT_VALUE_DECIMALS = 2;

/**
 * Measured living weight per daypart, with a fitted trend line.
 *
 * The measured series are drawn as points rather than lines, and the trend is the
 * only connected line — that is how the legacy chart distinguished the two, and
 * joining noisy daily measurements would otherwise read as a second trend.
 *
 * The trend follows whichever daypart is currently selected rather than a fixed
 * tag, reproducing the legacy mapper's "first visible daypart" rule.
 */
export function buildLivingWeightChartSpec(
    params: LivingWeightChartSpecParams
): MetricChartSpec<'line'> {
    const {
        days,
        graphedDaypartTags,
        activeDaypartTags,
        daypartLabel,
        locale,
        axisLabel,
        trendLabel,
        weightUnitTag,
    } = params;

    const measuredSeries = daypartMetricSeries({
        days,
        metric: 'weightAvg',
        graphedDaypartTags,
        activeDaypartTags,
        label: daypartLabel,
        keyPrefix: 'weightAvg',
        showLine: false,
    });

    const trendTag = trendDaypartTag(graphedDaypartTags, activeDaypartTags);
    const trend: MetricSeriesSpec = trendSeries({
        rows: trendTag ? days.filter((day) => day.daypartTag === trendTag) : [],
        day: (day) => day.day,
        value: (day) => day.data.weightTrend,
        label: trendLabel,
        color: daypartColor('trend_line'),
        key: 'weightTrend',
        dashed: false,
        showInLegend: true,
    });

    return {
        kind: 'line',
        days: dayAxis(days, (day) => day.day),
        series: [...measuredSeries, trend],
        left: {
            label: axisLabel,
            unit: weightUnitTag,
            tickDecimals: WEIGHT_TICK_DECIMALS,
            valueDecimals: WEIGHT_VALUE_DECIMALS,
        },
        locale,
    };
}

// ---------------------------------------------------------------------------
// Measurement count
// ---------------------------------------------------------------------------

export interface MeasurementsChartSpecParams extends DaypartChartSpecParams {
    axisLabel: string;
}

/** Number of fish measurements per day, one bar series per daypart. */
export function buildMeasurementsChartSpec(
    params: MeasurementsChartSpecParams
): MetricChartSpec<'bar'> {
    const { days, graphedDaypartTags, activeDaypartTags, daypartLabel, locale, axisLabel } = params;

    return {
        kind: 'bar',
        days: dayAxis(days, (day) => day.day),
        series: daypartMetricSeries({
            days,
            metric: 'measCnt',
            graphedDaypartTags,
            activeDaypartTags,
            label: daypartLabel,
            keyPrefix: 'measCnt',
        }),
        left: {
            label: axisLabel,
            // A count has no unit and no fractional part on either the axis or the
            // value, matching the legacy chart's `decimals: 0` on both.
            tickDecimals: 0,
            valueDecimals: 0,
        },
        locale,
    };
}

// ---------------------------------------------------------------------------
// Coefficient of variation
// ---------------------------------------------------------------------------

export interface CoefficientOfVariationChartSpecParams extends DaypartChartSpecParams {
    axisLabel: string;
}

const CV_UNIT = '%';

/**
 * Coefficient of variation of weight, one series per daypart.
 *
 * The API already reports this as a percentage — typically 20–35 — so no scaling
 * is applied. The legacy chart also carried `suggestedMaxMin({min: 0, max: 0.5})`,
 * which real data always exceeds and which therefore never had any effect; it is
 * deliberately not reproduced.
 */
export function buildCoefficientOfVariationChartSpec(
    params: CoefficientOfVariationChartSpecParams
): MetricChartSpec<'line'> {
    const { days, graphedDaypartTags, activeDaypartTags, daypartLabel, locale, axisLabel } = params;

    return {
        kind: 'line',
        days: dayAxis(days, (day) => day.day),
        series: daypartMetricSeries({
            days,
            metric: 'cv',
            graphedDaypartTags,
            activeDaypartTags,
            label: daypartLabel,
            keyPrefix: 'cv',
            showLine: false,
        }),
        left: {
            label: axisLabel,
            unit: CV_UNIT,
            beginAtZero: true,
            tickDecimals: 0,
            valueDecimals: 1,
        },
        locale,
    };
}
